import {HubConnection, HubConnectionBuilder, HubConnectionState} from "@microsoft/signalr";

type WaitingRoomStatusResponse = {
    redirectUri?: string | null;
    RedirectUri?: string | null;
};

export default class WaitingRoomController {
    private waitingRoomUrl: string;
    private connection: HubConnection;
    private isCompleted = false;

    waitingRoomId: string;


    constructor(waitingRoomId: string, hubUrl: string, waitingRoomUrl: string, completedMethod: string) {
        this.waitingRoomId = waitingRoomId;
        this.waitingRoomUrl = waitingRoomUrl;
        this.connection = new HubConnectionBuilder()
            .withUrl(hubUrl)
            .withAutomaticReconnect()
            .build();

        this.connection.on(completedMethod, uri => this.complete(uri));

        this.connection.onreconnected(async () => {
            try {
                await this.joinWaitingRoom();
            } catch (error) {
                console.warn("Unable to rejoin waiting room group.", error);
            }

            await this.checkWaitingRoom();
        });

        document.addEventListener("visibilitychange", async () => {
            if (!document.hidden) {
                await this.resume();
            }
        });
        window.addEventListener("pageshow", () => void this.resume());
        window.addEventListener("pagehide", () => void this.disconnect());
    }

    private async getWaitingRoom() {
        const response = await fetch(this.waitingRoomUrl, {cache: "no-store"});

        if (!response.ok) {
            throw new Error(`Waiting room status request failed with ${response.status}`);
        }

        return await response.json() as WaitingRoomStatusResponse;
    }

    async connect() {
        await this.connection.start();
        await this.joinWaitingRoom();
        await this.checkWaitingRoom();
    }

    async resume() {
        if (this.isCompleted) {
            return;
        }

        try {
            if (this.connection.state === HubConnectionState.Disconnected) {
                await this.connect();
                return;
            }
        } catch (error) {
            console.warn("Unable to reconnect to waiting room.", error);
        }

        await this.checkWaitingRoom();
    }

    async joinWaitingRoom() {
        if (this.connection.state !== HubConnectionState.Connected || this.isCompleted) {
            return;
        }

        await this.connection.invoke("JoinWaitingRoom", this.waitingRoomId);
    }

    async disconnect() {
        if (this.connection.state === HubConnectionState.Disconnected) {
            return;
        }

        try {
            if (this.connection.state === HubConnectionState.Connected) {
                await this.connection.invoke("LeaveWaitingRoom", this.waitingRoomId);
            }
        } catch (error) {
            console.warn("Unable to leave waiting room group before disconnecting.", error);
        }

        try {
            await this.connection.stop();
        } catch (error) {
            console.warn("Unable to stop waiting room connection.", error);
        }
    }

    async checkWaitingRoom() {
        if (this.isCompleted) {
            return;
        }

        let waitingRoom: WaitingRoomStatusResponse;
        try {
            waitingRoom = await this.getWaitingRoom();
        } catch (error) {
            console.warn("Unable to check waiting room status.", error);
            return;
        }

        const redirectUri = waitingRoom.redirectUri ?? waitingRoom.RedirectUri;

        if (redirectUri) {
            this.complete(redirectUri);
        }
    }

    private complete(uri: string) {
        if (this.isCompleted) {
            return;
        }

        console.log("redirecting to: " + uri);
        this.isCompleted = true;
        void this.disconnect();
        this.onCompleted(uri);
    }

    private onCompleted(uri: string) {
        window.location.href = uri;
    }
}
